import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { RideRow } from "@/components/driver/DriverHome";
import { Segmented } from "@/components/rider/Nav";
import { DAY_NAMES } from "@/lib/rider";

const startOfDay = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const monday = (d) => addDays(startOfDay(d), -((d.getDay() + 6) % 7));
const sameDay = (a, b) => startOfDay(a).getTime() === startOfDay(b).getTime();

function range(view, anchor) {
  if (view === "day") return [startOfDay(anchor), addDays(startOfDay(anchor), 1)];
  if (view === "week") return [monday(anchor), addDays(monday(anchor), 7)];
  const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  return [monday(first), addDays(monday(first), 42)];
}

function MonthGrid({ from, anchor, rides, selected, setSelected }) {
  return (
    <div className="grid grid-cols-7 gap-1" data-testid="calendar-month-grid">
      {DAY_NAMES.map((d) => <p key={d} className="text-center text-[10px] font-bold text-muted-foreground">{d}</p>)}
      {Array.from({ length: 42 }, (_, i) => addDays(from, i)).map((d) => {
        const n = rides.filter((r) => sameDay(new Date(r.scheduled_at), d)).length;
        const on = sameDay(d, selected);
        return (
          <button key={d.toISOString()} type="button" onClick={() => setSelected(d)} data-testid={`calendar-day-${d.getDate()}-${d.getMonth()}`}
            className={`flex aspect-square flex-col items-center justify-center rounded-lg text-xs transition-colors ${on ? "bg-primary text-primary-foreground" : d.getMonth() === anchor.getMonth() ? "bg-card hover:bg-muted" : "text-muted-foreground/50"}`}>
            {d.getDate()}
            {n > 0 && <span className={`mt-0.5 text-[9px] font-bold ${on ? "" : "text-primary"}`}>{n}</span>}
          </button>
        );
      })}
    </div>
  );
}

function DayList({ day, rides, onOpen }) {
  const list = rides.filter((r) => sameDay(new Date(r.scheduled_at), day));
  return (
    <section className="space-y-2" data-testid={`calendar-list-${day.getDate()}`}>
      <p className="text-xs font-bold uppercase tracking-[0.14em] text-muted-foreground">{day.toLocaleDateString([], { weekday: "long", month: "short", day: "numeric" })}</p>
      {!list.length && <p className="text-xs text-muted-foreground">No rides.</p>}
      {list.map((r) => <RideRow key={r.id} ride={r} onOpen={onOpen} />)}
    </section>
  );
}

export function DriverCalendar({ onOpen }) {
  const [view, setView] = useState("week");
  const [anchor, setAnchor] = useState(new Date());
  const [selected, setSelected] = useState(new Date());
  const [from, to] = range(view, anchor);
  const { data, isLoading } = useQuery({
    queryKey: ["driverCalendar", view, from.toISOString()],
    queryFn: () => api.get("/driver/calendar", { params: { start: from.toISOString(), end: to.toISOString() } }).then((r) => r.data),
  });
  const rides = data || [];
  const step = (n) => {
    const next = view === "day" ? addDays(anchor, n) : view === "week" ? addDays(anchor, 7 * n) : new Date(anchor.getFullYear(), anchor.getMonth() + n, 1);
    setAnchor(next);
    setSelected(next);
  };
  const title = view === "month" ? anchor.toLocaleDateString([], { month: "long", year: "numeric" })
    : view === "week" ? `${from.toLocaleDateString([], { month: "short", day: "numeric" })} – ${addDays(to, -1).toLocaleDateString([], { month: "short", day: "numeric" })}`
      : anchor.toLocaleDateString([], { weekday: "long", month: "short", day: "numeric" });
  return (
    <div className="space-y-4" data-testid="driver-calendar">
      <h1 className="font-display text-2xl font-bold tracking-tight">My Calendar</h1>
      <Segmented testid="calendar-view" value={view} onChange={setView} options={[{ value: "day", label: "DAY" }, { value: "week", label: "WEEK" }, { value: "month", label: "MONTH" }]} />
      <div className="flex items-center justify-between">
        <button type="button" onClick={() => step(-1)} className="grid h-9 w-9 place-items-center rounded-full hover:bg-muted" data-testid="calendar-prev"><ChevronLeft className="h-4 w-4" /></button>
        <p className="text-sm font-semibold" data-testid="calendar-title">{title}</p>
        <button type="button" onClick={() => step(1)} className="grid h-9 w-9 place-items-center rounded-full hover:bg-muted" data-testid="calendar-next"><ChevronRight className="h-4 w-4" /></button>
      </div>
      {isLoading && <Loader2 className="h-5 w-5 animate-spin text-primary" />}
      {view === "month" && <MonthGrid from={from} anchor={anchor} rides={rides} selected={selected} setSelected={setSelected} />}
      {view === "month" ? <DayList day={selected} rides={rides} onOpen={onOpen} />
        : Array.from({ length: view === "day" ? 1 : 7 }, (_, i) => addDays(from, i)).map((d) => <DayList key={d.toISOString()} day={d} rides={rides} onOpen={onOpen} />)}
    </div>
  );
}
